/**
 * The rail's layout in this browser: the collapsed groups and the hidden pages.
 *
 * **A cache, not the record.** The employee's settings document on the
 * backend holds both lists (ADR-0014). `src/lib/preference-store.ts` is the
 * only caller: it seeds the first paint from here, and writes here whenever
 * the reader changes the rail or the server answers.
 *
 * **This is the fourth module allowed to touch `localStorage`, and the split
 * is deliberate.** `src/auth/session-store.ts` holds the session,
 * `src/lib/theme-store.ts` holds one theme name,
 * `src/lib/column-preference-store.ts` holds column layouts, and this holds
 * the rail's two lists — collapsed group names and hidden page ids — and can
 * never hold anything else. ADR-0014 widened it from one list to two rather
 * than add another writer, because both lists are the same rail's layout. A
 * group name or a page id is not a credential, so this widens ADR-0003's
 * exemption to a named file rather than relaxing its rule —
 * `docs/decisions/0012-a-collapsed-rail-group-persists-per-browser.md`
 * carries the case, as ADR-0007 asked it to.
 *
 * `.oxlintrc.json` exempts this file for `localStorage` and nothing else, the
 * same narrowing the other three get: `sessionStorage`, `indexedDB`, `caches`,
 * `document.cookie`, every `window.`-prefixed spelling and the computed form
 * all still fail the gate here.
 *
 * **The COLLAPSED groups are stored, not the open ones.** A group opens by
 * default, so a group this list does not name is open — which means a rail
 * that gains a group shows it, rather than hiding it behind a record written
 * before it existed. A renamed or retired group leaves an entry that names
 * nothing, and an entry that names nothing changes no rail. Hidden pages work
 * the same way: a page the list does not name is shown.
 *
 * Nothing here trusts what it reads, and no entry point throws. The keys are
 * editable by hand and outlive the rail that wrote them, so a stale or
 * unreadable key must cost the reader their choice rather than their rail.
 */

/**
 * The key the collapsed groups live under.
 *
 * One key for every group rather than one per group, because a reader who
 * clears it clears their choices, and a per-group key leaves an origin full
 * of names nothing reads.
 */
export const NAV_GROUPS_KEY = 'ui-internal.nav-collapsed-groups'

/**
 * The key the hidden pages live under.
 *
 * A second key rather than a second field under the first, so that a browser
 * which already holds collapsed groups reads them unchanged.
 */
export const NAV_HIDDEN_PAGES_KEY = 'ui-internal.nav-hidden-pages'

/**
 * A list of names, from a value of unknown shape.
 *
 * Shared by this browser's keys and by the settings document the backend
 * returns, so the cache and the record are read by one rule. A name is
 * compared against the declared list, so a non-string entry can only ever fail
 * to match; dropping it keeps the list a `string[]` without an assertion.
 *
 * @param value - A parsed JSON value.
 * @returns The string entries, or null when the value is not a list.
 */
export function parseNameList(value: unknown): string[] | null {
  return Array.isArray(value)
    ? value.filter(entry => typeof entry === 'string')
    : null
}

/**
 * Read one stored list of names.
 *
 * Null and an empty list differ. Null is a browser that holds no choice; an
 * empty list is a reader who chose none, and that choice wins over another
 * machine's.
 *
 * @param key - The storage key.
 * @returns The names, or null when the key is absent or unusable.
 */
function readNameList(key: string): string[] | null {
  try {
    const raw = localStorage.getItem(key)

    // Storage disabled, and a key that was hand-edited or half-written by a
    // tab that died mid-save, all land in the catch and mean "no choice".
    return raw === null ? null : parseNameList(JSON.parse(raw))
  } catch {
    return null
  }
}

/**
 * Hold one list of names across reloads.
 *
 * @param key - The storage key.
 * @param names - The names to hold.
 * @returns True when the list was held, false when storage refused it.
 */
function writeNameList(key: string, names: readonly string[]): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(names))
  } catch {
    // A full quota, or storage disabled. The rail still works for this tab.
    return false
  }

  return true
}

/**
 * Forget one stored list, so the browser holds no choice.
 *
 * @param key - The storage key.
 */
function clearNameList(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    // Storage disabled: there is nothing held to forget.
  }
}

/** Forget the collapsed groups: this browser then holds no choice. */
export function clearCollapsedGroups(): void {
  clearNameList(NAV_GROUPS_KEY)
}

/** Forget the hidden pages: this browser then holds no choice. */
export function clearHiddenPages(): void {
  clearNameList(NAV_HIDDEN_PAGES_KEY)
}

/**
 * The groups this browser holds as collapsed.
 *
 * @returns The collapsed group names, or null when this browser holds no
 *   choice.
 */
export function readCollapsedGroups(): readonly string[] | null {
  return readNameList(NAV_GROUPS_KEY)
}

/**
 * Hold the collapsed groups across reloads.
 *
 * @param groups - The group names to store as collapsed.
 * @returns True when the choice was held, false when storage refused it.
 */
export function writeCollapsedGroups(groups: readonly string[]): boolean {
  return writeNameList(NAV_GROUPS_KEY, groups)
}

/**
 * The page ids this browser holds as hidden from the rail.
 *
 * @returns The hidden ids, or null when this browser holds no choice.
 */
export function readHiddenPages(): readonly string[] | null {
  return readNameList(NAV_HIDDEN_PAGES_KEY)
}

/**
 * Hold the hidden pages across reloads.
 *
 * @param pages - The page ids to store as hidden.
 * @returns True when the choice was held, false when storage refused it.
 */
export function writeHiddenPages(pages: readonly string[]): boolean {
  return writeNameList(NAV_HIDDEN_PAGES_KEY, pages)
}
